import React from 'react';

/**
 * Minimal, dependency-free markdown renderer for the agent's replies. Handles the
 * subset the agent actually emits: paragraphs, **bold**, *italic*, `code`,
 * `-`/`*`/`1.` bullet lists, and GFM-style pipe tables. Keeps the chat looking like
 * Claude/GPT without pulling a markdown lib into the Cloudscape bundle.
 */

/** A table row is a line containing a pipe (with content on at least one side). */
function isTableRow(line) {
  return /\|/.test(line) && /\S/.test(line.replace(/\|/g, ''));
}
/** The |---|:--:| separator row directly under a table header. */
function isTableDivider(line) {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
}
/** Split a pipe row into trimmed cells, dropping the leading/trailing empties. */
function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/** Render inline spans: **bold**, *italic*, `code`. */
function renderInline(text, keyBase) {
  const nodes = [];
  // Tokenize on the three inline markers.
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) nodes.push(<strong key={`${keyBase}-${i}`}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) nodes.push(<code key={`${keyBase}-${i}`} className="wafmon-md-code">{tok.slice(1, -1)}</code>);
    else nodes.push(<em key={`${keyBase}-${i}`}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export default function Markdown({ text }) {
  if (!text) return null;
  // Split into blocks on blank lines; group consecutive bullet lines into a list.
  const lines = text.split('\n');
  const blocks = [];
  let para = [];
  let list = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: 'p', text: para.join(' ') });
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push({ type: 'ul', items: list.slice() });
      list = [];
    }
  };

  for (let idx = 0; idx < lines.length; idx++) {
    const line = lines[idx].trimEnd();
    // Table: a pipe row immediately followed by a |---|---| divider. Consume the
    // whole contiguous table block here so stray pipes never leak as raw text.
    if (isTableRow(line) && idx + 1 < lines.length && isTableDivider(lines[idx + 1])) {
      flushPara();
      flushList();
      const header = splitRow(line);
      const rows = [];
      idx += 2; // skip header + divider
      while (idx < lines.length && isTableRow(lines[idx]) && !isTableDivider(lines[idx])) {
        rows.push(splitRow(lines[idx]));
        idx += 1;
      }
      idx -= 1; // for-loop will ++ back to the next unconsumed line
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.*)/);
    const numbered = line.match(/^\s*\d+\.\s+(.*)/);
    if (bullet || numbered) {
      flushPara();
      list.push((bullet || numbered)[1]);
    } else if (line.trim() === '') {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();

  return (
    <div className="wafmon-md">
      {blocks.map((b, i) => {
        if (b.type === 'ul') {
          return (
            <ul key={i} className="wafmon-md-ul">
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it, `${i}-${j}`)}</li>
              ))}
            </ul>
          );
        }
        if (b.type === 'table') {
          return (
            <table key={i} className="wafmon-md-table">
              <thead>
                <tr>
                  {b.header.map((h, j) => (
                    <th key={j}>{renderInline(h, `th-${i}-${j}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((c, j) => (
                      <td key={j}>{renderInline(c, `td-${i}-${r}-${j}`)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        return (
          <p key={i} className="wafmon-md-p">
            {renderInline(b.text, `p-${i}`)}
          </p>
        );
      })}
    </div>
  );
}
